import dispatchGameContext from '@/api/aggregation/helpers/dispatch-game-context';
import {
  getGameHelpData,
  getGameWindowData,
} from '@/api/aggregation/helpers/parse-aggregation-game-window-data';
import { dispatchSWJData } from '@/api/aggregation/swj/dispatch-swj-data';
import { ExtendedGameInfo } from '@/api/aggregation/types';
import { setIsJackpotModeSuppressed } from '@/store/actions';
import { useStore } from '@/store/useStore';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import {
  GameEntityData,
  GameWindowDataResponse,
  ProviderData,
} from '@/api/game-window-data/types';

export const applyGameWindowData = (composite: GameWindowDataResponse) => {
  const game = composite?.game;
  if (!game?.uid) {
    return;
  }

  const { platform } = useStore.getState().runtime;
  const {
    superclass: previousSuperclass,
    showNetPosition: previousShowNetPosition,
  } = useStore.getState().gameContext;
  const gameData: GameEntityData = game.data ?? {};
  const providerData: ProviderData = composite?.providerData?.data ?? {};

  const missingSuperclass = !gameData.superclass?.uid;
  const missingShowNetPosition = providerData.show_net_position == null;

  const superclass = missingSuperclass
    ? previousSuperclass
      ? { uid: previousSuperclass }
      : undefined
    : gameData.superclass;

  const showNetPosition = missingShowNetPosition
    ? (previousShowNetPosition ?? undefined)
    : providerData.show_net_position;

  if (missingSuperclass || missingShowNetPosition) {
    Logger.logRumError({
      name: '[GW][GAME_SWITCH]: Net position fields missing',
      description:
        'game-window-data composite omitted net-position gate fields; preserved the previous game values',
      useCase: DdEventCategory.GameSwitch,
      eventType: DdEventType.Error,
      context: {
        gameUid: game.uid,
        missingSuperclass,
        missingShowNetPosition,
      },
    });
  }

  const gameInfo: ExtendedGameInfo = {
    ...gameData,
    gameUid: game.uid,
    documentId: game.id,
    platform,
    superclass,
    provider: {
      providerName: providerData.display_name,
      show_net_position: showNetPosition,
    },
  };

  const gwData = composite?.gameWindow?.data as Parameters<
    typeof getGameWindowData
  >[0];

  dispatchGameContext({
    gameInfo,
    gameWindowData: getGameWindowData(gwData),
    gameHelpData: getGameHelpData(gwData),
  });

  setIsJackpotModeSuppressed(false);

  const jackpotId = gameData.siteWideJackpotId;
  const jackpotEntry = jackpotId
    ? composite?.jackpotData?.[jackpotId]
    : undefined;

  if (jackpotId && !jackpotEntry) {
    Logger.logRumError({
      name: '[GW][GAME_SWITCH]: Jackpot entry missing',
      description:
        'game-window-data composite referenced a siteWideJackpotId with no matching jackpotData entry',
      useCase: DdEventCategory.GameSwitch,
      eventType: DdEventType.Error,
      context: { gameUid: game.uid, jackpotId },
    });
  }

  dispatchSWJData(jackpotEntry?.data, composite?.features);
};

export default applyGameWindowData;
